import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Input,
  Output,
  EventEmitter
} from '@angular/core';
import { ConfirmationService } from 'primeng/api';
import { ConversationListService } from '../../services/conversation-list.service';
import { Conversation } from '../../Conversation';
import { ActivatedRoute, Params, Router } from '@angular/router';
//var _ = require('lodash');

export type ConversationTab = 'direct' | 'stream' | 'ai';

@Component({
  selector: 'app-conversation-list',
  templateUrl: './conversation-list.component.html',
  styleUrls: ['./conversation-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ConversationListComponent implements OnInit {
  // Only offered while the AI conversation is open - startNewAiSession() needs a live aiMode
  // <mi-chat> to act on, so there is nothing for it to reset from anywhere else.
  @Input() aiMode = false;
  @Output() askAi = new EventEmitter<void>();
  @Output() newAiChat = new EventEmitter<void>();

  directConversations: Conversation[] = [];
  streamConversations: Conversation[] = [];
  activeTab: ConversationTab = 'direct';
  skipTokens = {
    direct: null,
    stream: null
  };

  constructor(
    private conversationListService: ConversationListService,
    private confirmationService: ConfirmationService,
    public route: ActivatedRoute,
    public router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  // The route's own id, kept separately from activeTab - direct/streamConversations aren't
  // populated yet when route.params first fires on a reload, so which tab it belongs to can't
  // be decided until the lists load too (see syncActiveTabToRoute()).
  private currentRouteId: string | undefined;

  ngOnInit() {
    // Deep-linking straight into a conversation (a page reload, or clicking a sidebar item)
    // leaves activeTab on its 'direct' default otherwise, so e.g. reloading a stream conversation
    // shows the Chat tab while a *different* conversation's row is what's actually highlighted -
    // keep the tab selector in sync with the route instead of only ever changing on a manual
    // tab click.
    this.route.params.subscribe((params: Params) => {
      this.currentRouteId = params['id'];
      this.syncActiveTabToRoute();
    });

    this.conversationListService.ready$.subscribe(async () => {
      console.log('SERVICE IS READY');
      await this.refreshConversations();
      this.syncActiveTabToRoute();

      this.conversationListService.conversationsDeleted$.subscribe(resourceId => {
        this.removeConversation(resourceId, true);
        this.removeConversation(resourceId, false);
        this.cdr.markForCheck();
      });

      this.conversationListService.conversationsChanged$.subscribe(conversation => {
        this.removeConversation(conversation.id, conversation.stream);
        this.addNewConversation(conversation, conversation.stream);
        this.syncActiveTabToRoute();
        this.cdr.markForCheck();
      });
    });
  }

  // Decides which tab the current route's conversation belongs to. Falls through to 'direct'
  // when the id isn't found in either list yet (lists still loading) or at all (e.g. a brand
  // new conversation not yet round-tripped into streamConversations/directConversations) -
  // only overrides an already-correct 'stream'/'ai' tab if the id has since moved off it.
  private syncActiveTabToRoute(): void {
    const id = this.currentRouteId;
    if (id === 'ai-mode-demo') {
      this.activeTab = 'ai';
    } else if (id && this.streamConversations.some(c => c.id === id)) {
      this.activeTab = 'stream';
    } else if (id && this.directConversations.some(c => c.id === id)) {
      this.activeTab = 'direct';
    } else if (this.activeTab === 'ai') {
      this.activeTab = 'direct';
    }
    this.cdr.markForCheck();
  }

  removeConversation(id: string, stream: boolean) {
    let array = stream ? this.streamConversations : this.directConversations;

    array = array.filter(item => {
      return item.id != id;
    });

    this.updateGlobalConversationListVariables(stream, array);
  }

  addNewConversation(conversation: Conversation, stream: boolean, toTop = true) {
    let array = stream ? this.streamConversations : this.directConversations;

    toTop ? array.unshift(conversation) : array.push(conversation);

    this.updateGlobalConversationListVariables(stream, array);
  }

  updateGlobalConversationListVariables(stream, array) {
    if (stream) {
      this.streamConversations = array;
    } else {
      this.directConversations = array;
    }
  }

  async refreshConversations(stream?, loadMore?) {
    if (stream == null) {
      await this.updateConversationList(true, loadMore);
      await this.updateConversationList(false, loadMore);
    } else {
      await this.updateConversationList(stream, loadMore);
    }
  }

  async updateConversationList(stream, loadMore?) {
    let conversationList = stream ? this.streamConversations : this.directConversations;

    let result = await this.conversationRefreshResult(stream, loadMore);
    let conversations = result[0] as Conversation[];

    if (stream) {
      this.skipTokens.stream = result[1];
    } else {
      this.skipTokens.direct = result[1];
    }

    for (var i = 0; i < conversations?.length; i++) {
      conversationList.push(conversations[i]);
    }
    this.cdr.markForCheck();
  }

  async conversationRefreshResult(stream, loadMore?) {
    let params = {};
    if (loadMore) {
      params['skipToken'] = stream ? this.skipTokens.stream : this.skipTokens.direct;
    }

    params['filter'] = stream ? 'stream eq true' : 'stream eq false';

    let result = await this.conversationListService.getConversations(params);

    return result;
  }

  showLoadMore() {
    return this.activeTab === 'stream' ? this.skipTokens.stream != null : this.skipTokens.direct != null;
  }

  confirmDelete(event: Event, conversation: Conversation): void {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      header: 'Delete Conversation',
      message: 'This conversation will be permanently deleted. This action cannot be undone.',
      acceptButtonProps: { label: 'Delete', severity: 'danger' },
      rejectButtonProps: { label: 'Cancel', text: true },
      accept: () => this.deleteConversation(conversation)
    });
  }

  private async deleteConversation(conversation: Conversation): Promise<void> {
    await this.conversationListService.deleteConversation(conversation.id);
    this.removeConversation(conversation.id, conversation.stream);
    this.cdr.markForCheck();
  }
}
